import {announce} from '@github-ui/aria-live'
import {Heading} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect} from 'react'

import type {MemexColumnDataType} from '../../../api/columns/contracts/memex-column'
import {CommitState} from '../../../hooks/use-autosave'
import {useSingleSelectOptions} from '../../../hooks/use-single-select-options'
import type {ColumnModelForDataType} from '../../../models/column-model'
import {AutosaveMessage} from '../autosave-message'
import {SingleSelectForm} from './single-select-form'
import styles from './single-select-options.module.css'

type ColumnSettingsViewProps = {
  column: ColumnModelForDataType<typeof MemexColumnDataType.SingleSelect>
  disabled?: boolean
}

export const SingleSelectOptions: React.FC<ColumnSettingsViewProps> = ({column: externalColumn, disabled = false}) => {
  const {
    commitState,
    // column settings
    options,
    onDrop,
    addOption,
    canRemoveOption,
    removeOption,
    updateOption,
    // error message
    errorMessage,
  } = useSingleSelectOptions({externalColumn})

  useEffect(() => {
    if (commitState === CommitState.Successful) {
      announce('Saved!')
    }
  }, [commitState])

  return (
    <>
      <Heading as="h3" className={styles.OptionsHeading} aria-label={`${externalColumn.name} options`}>
        Options
      </Heading>
      <SingleSelectForm
        options={options}
        onAddOption={addOption}
        onUpdateOption={updateOption}
        onRemoveOption={removeOption}
        canRemoveOption={canRemoveOption}
        onDrop={onDrop}
        className={clsx(commitState === CommitState.Failed && styles.SingleSelectForm)}
        noDuplicatedOptions={false}
        disabled={disabled}
        enableNameLengthValidation={false}
        enableDescriptionLengthValidation={false}
      />

      {!disabled && <AutosaveMessage commitState={commitState} errorMessage={errorMessage} />}
    </>
  )
}

try{ SingleSelectOptions.displayName ||= 'SingleSelectOptions' } catch {}